// Thin typed wrapper over `api` for every backend endpoint. Each call returns
// the JSON payload directly; ApiError surfaces server errors with a `code` we
// can branch on (e.g. "permission_denied", "failed_precondition").

import { api } from "./api";

// ── Shapes (loose: backend uses camelCase Firestore docs) ──────────────────

export type AccountType =
  | "platform_admin"
  | "individual"
  | "institution"
  | "enterprise_owner"
  | "enterprise_member";

export type Tier = {
  slug: "tier-1" | "tier-2";
  name: string;
  pricePerSeatEur: number;
  monthlyCreditsLimit?: number;
  storageBytesLimit?: number;
  requestsPerMinuteLimit?: number;
  features?: Record<string, unknown>;
};

export type Org = {
  id: string;
  name: string;
  type: "enterprise" | "institution" | "free";
  status: "active" | "suspended";
  ownerUid?: string;
  ownerId?: string;
  activeTier: "tier-1" | "tier-2" | null;
  seatCount: number;
  fiscal?: OrgFiscal | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionStatus?: string | null;
  defaultPoolId?: string | null;
  designPartner?: boolean;
  onboardingCompleted?: boolean;
  onboardingCompletedAt?: unknown;
  createdAt?: unknown;
};

export type OrgRole = "owner" | "admin" | "member" | "external";
export type OveragePolicy = "block" | "billed";

export type DbPool = {
  id?: string;
  orgId: string;
  scope: "org" | "workspace";
  scopeRefId: string;
  tierSlug: string | null;
  monthlyCreditsLimit: number;
  storageBytesLimit: number;
  rpmLimit: number;
  perUserCapPct: number;
  overagePolicy: OveragePolicy;
  overageEurPerMillionCredits: number;
  alertThresholds: number[];
  alertsFiredFor?: Record<string, number[]>;
  allocationMode: "equal" | "manual";
  allocations: Record<string, { pct?: number; abs?: number }>;
  killSwitch: boolean;
  storageFull: boolean;
  currentPeriod: string;
  creditsUsedThisMonth: number;
  storageBytesUsed: number;
};

export type DbWorkspace = {
  id: string;
  orgId: string;
  name: string;
  createdByUid: string;
  memberAccess: string[];
  poolId: string | null;
};

export type TaxIdType =
  | "es_cif"
  | "es_dni"
  | "eu_vat"
  | "gb_vat"
  | "ch_vat"
  | "us_ein"
  | "no_vat";

export type OrgFiscal = {
  nif: string;
  taxIdType?: TaxIdType;
  razonSocial: string;
  direccionFiscal: {
    line1: string;
    line2: string | null;
    city: string;
    postalCode: string;
    region: string | null;
  };
  pais: string;
  emailFacturacion: string;
  viesValidated?: boolean;
};

export type Member = {
  uid: string;
  email?: string | null;
  role: OrgRole;
  status: "active" | "revoked";
  addedAt?: unknown;
  usage?: {
    creditsUsed?: number;
    requestsCount?: number;
  };
};

export type Invite = {
  id: string;
  email: string;
  kind?: "org_owner" | "member";
  orgId?: string;
  orgName?: string;
  designPartner?: boolean;
  status: "pending" | "accepted";
  invitedAt?: unknown;
};

export type ApiKey = {
  keyHash: string;
  orgId: string;
  ownerUid: string;
  name: string;
  keyPrefix: string;
  keyLast4: string;
  status: "active" | "revoked";
  createdAt?: unknown;
};

export type Proposal = {
  id: string;
  orgId: string;
  concept: string;
  amountEur: number;
  ivaRate: number;
  status: "sent" | "paid" | "expired" | "canceled";
  paymentLink?: string;
  invoicePdf?: string | null;
  invoiceHostedUrl?: string | null;
  createdAt?: unknown;
  paidAt?: unknown;
};

export type Subscription = {
  id: string;
  orgId: string;
  orgName?: string | null;
  tierSlug?: string;
  quantity: number;
  status: string;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: unknown;
  stripeCustomerId?: string;
};

// ── Auth ─────────────────────────────────────────────────────────────────

export const Auth = {
  // displayName is optional now: when omitted/empty the backend falls back
  // to the email-prefix and the in-app onboarding (StepName) overrides it
  // via Profile.updateDisplayName.
  initialize: (token: string, displayName?: string) =>
    api.post<{ status: string; orgId?: string; role?: string }>(
      "/auth/initialize",
      { token, displayName: displayName ?? "" },
    ),
  invitePreview: (token: string) =>
    api.get<{
      email: string;
      kind: "org_owner" | "member";
      orgName: string | null;
      expiresAt: string | null;
    }>(`/auth/invite/${encodeURIComponent(token)}`),
  me: () =>
    api.get<{
      auth: {
        uid: string;
        email: string | null;
        isPlatformAdmin: boolean;
        accountType: AccountType | null;
        enterpriseId: string | null;
        institutionId: string | null;
      };
      profile: Record<string, unknown> | null;
    }>("/auth/me"),
};

// ── Admin invites (org owners) ──────────────────────────────────────────

export const Invites = {
  list: () => api.get<{ invites: Invite[] }>("/admin/invites"),
  create: (input: { email: string; orgName: string; designPartner?: boolean }) =>
    api.post<{ status: string; email: string }>("/admin/invites", input),
  revoke: (email: string) =>
    api.delete<{ status: string }>(
      `/admin/invites/${encodeURIComponent(email)}`,
    ),
};

export const Profile = {
  updateDisplayName: (displayName: string) =>
    api.patch<{ ok: true }>("/auth/me/profile", { displayName }),
};

export const Onboarding = {
  complete: (orgId: string) =>
    api.post<{ ok: true; alreadyCompleted?: boolean }>(
      `/orgs/${orgId}/onboarding/complete`,
      {},
    ),
};

// ── Org / fiscal / Stripe customer ──────────────────────────────────────

export const Orgs = {
  get: (orgId: string) => api.get<{ org: Org }>(`/orgs/${orgId}`),
  updateFiscal: (
    orgId: string,
    fiscal: OrgFiscal,
    extra?: {
      consent?: { version: string; accepted: true };
      taxIdType?: string;
    },
  ) =>
    api.patch<{
      ok: boolean;
      viesValidated: boolean;
      stripeCustomerCreated: boolean;
    }>(`/orgs/${orgId}/fiscal`, { ...fiscal, ...extra }),
  invoices: (orgId: string) =>
    api.get<{
      invoices: Array<{
        id: string;
        number: string | null;
        kind: "subscription" | "oneoff";
        status: "open" | "paid" | "uncollectible" | string;
        total: number;
        currency: string;
        description: string;
        created: string | null;
        paidAt: string | null;
        hostedUrl: string | null;
        pdfUrl: string | null;
      }>;
    }>(`/orgs/${orgId}/invoices`),
  ensureStripeCustomer: (orgId: string) =>
    api.post<{ customerId: string; created: boolean }>(
      `/orgs/${orgId}/stripe-customer`,
    ),
};

// ── Proposals ────────────────────────────────────────────────────────────

export const Proposals = {
  list: () => api.get<{ proposals: Proposal[] }>("/proposals"),
  create: (input: {
    orgId: string;
    concept: string;
    amountEur: number;
    ivaRate?: number;
    expiresInDays?: number;
  }) =>
    api.post<{ proposalId: string; paymentLink?: string }>("/proposals", input),
  update: (
    proposalId: string,
    input: {
      concept: string;
      amountEur: number;
      ivaRate?: number;
      expiresInDays?: number;
    },
  ) =>
    api.patch<{ proposalId: string; paymentLink?: string }>(
      `/proposals/${proposalId}`,
      input,
    ),
  cancel: (proposalId: string) =>
    api.delete<{ ok: boolean }>(`/proposals/${proposalId}`),
};

// ── Subscriptions ────────────────────────────────────────────────────────

export type CatalogTier = {
  id: string;
  slug: string;
  name: string;
  pricePerSeatEur: number;
  monthlyCreditsLimit: number;
  storageBytesLimit: number;
  requestsPerMinuteLimit: number;
  features: Record<string, unknown>;
};

export type TierPatch = {
  pricePerSeatEur?: number;
  monthlyCreditsLimit?: number;
  storageBytesLimit?: number;
  requestsPerMinuteLimit?: number;
  features?: { artifacts?: boolean; prioritySupport?: boolean };
};

export type TierCreate = {
  slug: string;
  name: string;
  pricePerSeatEur: number;
  monthlyCreditsLimit: number;
  storageBytesLimit: number;
  requestsPerMinuteLimit: number;
  features: { artifacts?: boolean; prioritySupport?: boolean };
};

// ── Admin: model pricing + global pricing (credit conversion) ───────────

export type TokenType =
  | "input"
  | "output"
  | "cachedRead"
  | "cacheCreation"
  | "thinking";

export type ModelPricing = {
  id: string;
  modelId: string;
  provider: "anthropic" | "openai" | "google";
  displayName: string;
  prices: Record<TokenType, number>;
  multiplierOverrides?: Partial<Record<TokenType, number>>;
  isActive: boolean;
  deprecated?: boolean;
};

export type GlobalPricingConfig = {
  usdPerCredit: number | null;
  baselineModelId: string | null;
  baselineTokenType: TokenType | null;
};

export const AdminModelPricing = {
  list: () =>
    api.get<{ models: ModelPricing[] }>("/admin/model-pricing"),
  create: (body: {
    modelId: string;
    provider: ModelPricing["provider"];
    displayName: string;
    prices: Record<TokenType, number>;
    multiplierOverrides?: Partial<Record<TokenType, number>>;
    isActive?: boolean;
  }) => api.post<{ modelId: string }>("/admin/model-pricing", body),
  update: (
    modelId: string,
    patch: Partial<{
      provider: ModelPricing["provider"];
      displayName: string;
      prices: Partial<Record<TokenType, number>>;
      multiplierOverrides: Partial<Record<TokenType, number>>;
      isActive: boolean;
      deprecated: boolean;
    }>,
  ) =>
    api.patch<{ modelId: string }>(
      `/admin/model-pricing/${encodeURIComponent(modelId)}`,
      patch,
    ),
  deprecate: (modelId: string) =>
    api.delete<{ modelId: string; deprecated: boolean }>(
      `/admin/model-pricing/${encodeURIComponent(modelId)}`,
    ),
  seed: () =>
    api.post<{ created: string[]; skipped: string[]; globalCreated: boolean }>(
      "/admin/model-pricing/seed",
      {},
    ),
};

export const AdminGlobalPricing = {
  get: () => api.get<{ config: GlobalPricingConfig }>("/admin/global-pricing"),
  update: (patch: Partial<GlobalPricingConfig>) =>
    api.patch<{ ok: boolean }>("/admin/global-pricing", patch),
};

export const AdminTiers = {
  list: () =>
    api.get<{
      tiers: (CatalogTier & { stripePriceId: string })[];
    }>("/admin/tiers"),
  create: (body: TierCreate) =>
    api.post<{
      slug: string;
      stripePriceId: string;
      stripeProductId: string;
    }>("/admin/tiers", body),
  update: (slug: string, patch: TierPatch) =>
    api.patch<{
      slug: string;
      affectedSubscriptions: number;
      newStripePriceId: string | null;
    }>(`/admin/tiers/${slug}`, patch),
};

export const Subscriptions = {
  catalog: () => api.get<{ tiers: CatalogTier[] }>("/tiers"),
  acceptTerms: (orgId: string, version = "v1") =>
    api.post<{ status: string; version: string }>(
      `/orgs/${orgId}/subscription/accept-terms`,
      { version },
    ),
  get: (orgId: string) =>
    api.get<{
      orgId: string;
      activeTier: string | null;
      seatCount: number;
      subscriptionStatus: string | null;
      stripeSubscriptionId: string | null;
      tier: Tier | null;
      subscription: Subscription | null;
    }>(`/orgs/${orgId}/subscription`),
  checkout: (
    orgId: string,
    body?: { successUrl?: string; cancelUrl?: string },
  ) =>
    api.post<{ checkoutUrl: string; checkoutSessionId: string }>(
      `/orgs/${orgId}/subscription/checkout`,
      body ?? {},
    ),
  portal: (orgId: string, body?: { returnUrl?: string }) =>
    api.post<{ portalUrl: string }>(
      `/orgs/${orgId}/subscription/portal`,
      body ?? {},
    ),
  cancel: (orgId: string, immediate = false) =>
    api.delete<{ status: string; cancelAtPeriodEnd: boolean }>(
      `/orgs/${orgId}/subscription${immediate ? "?immediate=true" : ""}`,
    ),
  listAll: () =>
    api.get<{ subscriptions: Subscription[] }>("/admin/subscriptions"),
};

// ── Team (members) ───────────────────────────────────────────────────────

export const Team = {
  list: (orgId: string) =>
    api.get<{
      orgId: string;
      seatCount: number;
      seatsInUse: number;
      activeTier: string | null;
      tier: Tier | null;
      pool: DbPool | null;
      members: Member[];
      invites: Invite[];
    }>(`/orgs/${orgId}/members`),
  invite: (
    orgId: string,
    email: string,
    role: "admin" | "member" | "external" = "member",
  ) =>
    api.post<{ status: string; role: string }>(`/orgs/${orgId}/members`, {
      email,
      role,
    }),
  cancelInvite: (orgId: string, email: string) =>
    api.delete<{ status: string }>(
      `/orgs/${orgId}/invites/${encodeURIComponent(email)}`,
    ),
  revoke: (orgId: string, uid: string) =>
    api.delete<{ status: string }>(`/orgs/${orgId}/members/${uid}`),
  setRole: (orgId: string, uid: string, role: "admin" | "member" | "external") =>
    api.patch<{ status: string; role: string }>(
      `/orgs/${orgId}/members/${uid}/role`,
      { role },
    ),
};

// ── Pool (org credit + storage shared budget) ───────────────────────────

export const Pools = {
  get: (orgId: string) =>
    api.get<{ pool: DbPool }>(`/orgs/${orgId}/pool`),
  usage: (orgId: string, period?: string) =>
    api.get<{
      poolId: string;
      period: string;
      pool: DbPool;
      aggregate: {
        totalCreditsUsed?: number;
        totalRequests?: number;
        storageBytesUsed?: number;
        byMember?: Record<
          string,
          { creditsUsed?: number; requestsCount?: number }
        >;
      } | null;
    }>(
      `/orgs/${orgId}/pool/usage${period ? `?period=${encodeURIComponent(period)}` : ""}`,
    ),
  me: (orgId: string) =>
    api.get<{
      poolId: string;
      role: OrgRole;
      period: string;
      monthlyCreditsLimit: number;
      creditsUsedThisMonth: number;
      storageBytesLimit: number;
      storageBytesUsed: number;
      overagePolicy: OveragePolicy;
      killSwitch: boolean;
      storageFull: boolean;
      memberUsage: { creditsUsed?: number; requestsCount?: number };
    }>(`/orgs/${orgId}/pool/me`),
  updatePolicy: (
    orgId: string,
    patch: {
      perUserCapPct?: number;
      overagePolicy?: OveragePolicy;
      overageEurPerMillionCredits?: number;
      alertThresholds?: number[];
    },
  ) =>
    api.patch<{ status: string }>(`/orgs/${orgId}/pool`, patch),
  updateAllocations: (
    orgId: string,
    allocationMode: "equal" | "manual",
    allocations?: Record<string, { pct?: number; abs?: number }>,
  ) =>
    api.patch<{ status: string }>(`/orgs/${orgId}/pool/allocations`, {
      allocationMode,
      allocations,
    }),
  resetKillSwitch: (orgId: string) =>
    api.post<{ status: string }>(
      `/orgs/${orgId}/pool/reset-killswitch`,
      {},
    ),
};

// ── Workspaces (Phase 2 structural — UI minimal in Phase 1) ─────────────

export const Workspaces = {
  list: (orgId: string) =>
    api.get<{ workspaces: DbWorkspace[] }>(`/orgs/${orgId}/workspaces`),
  create: (
    orgId: string,
    body: { name: string; memberAccess?: string[] },
  ) => api.post<DbWorkspace>(`/orgs/${orgId}/workspaces`, body),
  update: (
    orgId: string,
    wsId: string,
    patch: { name?: string; memberAccess?: string[] },
  ) =>
    api.patch<{ status: string }>(
      `/orgs/${orgId}/workspaces/${wsId}`,
      patch,
    ),
  delete: (orgId: string, wsId: string) =>
    api.delete<{ status: string }>(`/orgs/${orgId}/workspaces/${wsId}`),
};

// ── Admin: orgs ──────────────────────────────────────────────────────────

export const AdminOrgs = {
  list: (params?: { type?: "enterprise" | "free" | "all"; pageSize?: number }) => {
    const q = new URLSearchParams();
    if (params?.type) q.set("type", params.type);
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    return api.get<{ orgs: Org[]; total: number }>(
      `/admin/orgs${q.toString() ? `?${q}` : ""}`,
    );
  },
  details: (orgId: string) =>
    api.get<{
      org: Org;
      members: Member[];
      keys: ApiKey[];
      seatsInUse: number;
    }>(`/admin/orgs/${orgId}`),
  suspend: (orgId: string, action: "suspend" | "reactivate") =>
    api.patch<{ status: string }>(`/admin/orgs/${orgId}/suspend`, { action }),
  delete: (orgId: string) =>
    api.delete<{ status: string }>(`/admin/orgs/${orgId}`),
  setPlan: (
    orgId: string,
    activeTier: "tier-1" | "tier-2" | null,
    seatCount: number,
  ) =>
    api.patch<{
      orgId: string;
      activeTier: string | null;
      seatCount: number;
      seatsInUse: number;
    }>(`/admin/orgs/${orgId}/plan`, { activeTier, seatCount }),
  promote: (targetUid: string, orgName: string) =>
    api.post<{ status: string; orgId: string }>(`/admin/orgs/promote`, {
      targetUid,
      orgName,
    }),
  transfer: (orgId: string, newOwnerUid: string) =>
    api.post<{ status: string; orgId: string; newOwnerUid: string }>(
      `/admin/orgs/${orgId}/transfer`,
      { newOwnerUid },
    ),
};

// ── Admin: users / finance / audit ───────────────────────────────────────

export const AdminUsers = {
  list: (params?: { pageSize?: number; startAfterId?: string }) => {
    const q = new URLSearchParams();
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    if (params?.startAfterId) q.set("startAfterId", params.startAfterId);
    return api.get<{ users: Array<Record<string, unknown> & { uid: string }> }>(
      `/admin/users${q.toString() ? `?${q}` : ""}`,
    );
  },
  delete: (uid: string) =>
    api.delete<{ status: string }>(`/admin/users/${uid}`),
};

export const AdminFinance = {
  metrics: () =>
    api.get<{
      mrr: { eur: number; liveSubscriptions: number; seatsByTier: Record<string, number> };
      cancellations30d: number;
      pastDueCount: number;
      oneOffRevenue30dEur: number;
      tokensConsumed30d: number;
      generatedAt: string;
    }>("/admin/finance"),
  audit: (params?: { pageSize?: number; resourceId?: string }) => {
    const q = new URLSearchParams();
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    if (params?.resourceId) q.set("resourceId", params.resourceId);
    return api.get<{
      logs: Array<{
        id: string;
        actorUid: string;
        action: string;
        resourceId: string;
        timestamp?: unknown;
      }>;
    }>(`/admin/finance/audit${q.toString() ? `?${q}` : ""}`);
  },
};
